import Vue from "vue";
import LunchApi from "./lunchApiService";
import {
  LUNCH_CATEGORY_OPTIONS,
  LUNCH_FOOD_STATUS,
  getDefaultFoodImage
} from "./lunchFoodAssets";

export { LUNCH_FOOD_STATUS, getDefaultFoodImage };

export const lunchMenuState = Vue.observable({
  foods: [],
  calendarDateMetadata: {},
  menuSchedules: {},
  loadedFoodList: false
});

export function getLunchFoodCategories() {
  return LUNCH_CATEGORY_OPTIONS.map((item) => item.label);
}

export async function loadLunchFoods(params = {}) {
  const response = await LunchApi.listFoods(params);
  lunchMenuState.foods = response.result || [];
  lunchMenuState.loadedFoodList = true;
  return response;
}

export function getActiveLunchFoods() {
  return lunchMenuState.foods.filter(
    (food) => food.status === LUNCH_FOOD_STATUS.ACTIVE
  );
}

export function getLunchFoodById(foodId) {
  return lunchMenuState.foods.find((food) => food.id === foodId);
}

export async function loadMenuCalendar(monthKey) {
  const response = await LunchApi.getMenuCalendar(monthKey);
  const menuDates = response.menu_dates || [];
  const dateMetadata = response.dates || [];
  dateMetadata.forEach((date) => {
    if (date && date.date) {
      Vue.set(lunchMenuState.calendarDateMetadata, date.date, date);
    }
  });
  menuDates.forEach((dateKey) => {
    if (!lunchMenuState.menuSchedules[dateKey]) {
      Vue.set(lunchMenuState.menuSchedules, dateKey, ["__has_menu__"]);
    }
  });
  Object.keys(lunchMenuState.menuSchedules).forEach((dateKey) => {
    if (dateKey.indexOf(`${monthKey}-`) === 0 && !menuDates.includes(dateKey)) {
      Vue.set(lunchMenuState.menuSchedules, dateKey, []);
    }
  });
  return response;
}

export function getLunchDateMetadata(dateKey) {
  return lunchMenuState.calendarDateMetadata[dateKey] || null;
}

export async function getLunchMenuByDate(dateKey) {
  const response = await LunchApi.getMenuDetail(dateKey);
  Vue.set(lunchMenuState.menuSchedules, dateKey, response.food_ids || []);
  return response.foods || [];
}

export function getMenuFoodIdsByDate(dateKey) {
  return lunchMenuState.menuSchedules[dateKey]
    ? lunchMenuState.menuSchedules[dateKey].filter((id) => id !== "__has_menu__")
    : [];
}

export async function getMenusByDates(dateKeys) {
  const result = {};
  await Promise.all(
    dateKeys.map(async (dateKey) => {
      try {
        const menu = await LunchApi.getMenuDetail(dateKey);
        Vue.set(lunchMenuState.menuSchedules, dateKey, menu.food_ids || []);
        result[dateKey] = menu.food_ids || [];
      } catch (error) {
        result[dateKey] = [];
      }
    })
  );
  return result;
}

export function hasExistingMenu(dateKey) {
  return Boolean(
    lunchMenuState.menuSchedules[dateKey] &&
      lunchMenuState.menuSchedules[dateKey].length
  );
}

export function getExistingMenuDates(dateKeys) {
  return dateKeys.filter((dateKey) => hasExistingMenu(dateKey));
}

export async function saveMenuForDates(dateKeys, foodIds, overwrite = false) {
  await LunchApi.saveMenusBulk(dateKeys, foodIds, overwrite);
  dateKeys.forEach((dateKey) => {
    Vue.set(lunchMenuState.menuSchedules, dateKey, [...new Set(foodIds)]);
  });
}

export async function updateMenuForDate(dateKey, foodIds) {
  await LunchApi.updateMenu(dateKey, [...new Set(foodIds)]);
  Vue.set(lunchMenuState.menuSchedules, dateKey, [...new Set(foodIds)]);
}

export async function deleteMenuForDate(dateKey) {
  await LunchApi.deleteMenu(dateKey);
  Vue.set(lunchMenuState.menuSchedules, dateKey, []);
}

export async function createLunchFood(foodData) {
  const food = await LunchApi.createFood(foodData);
  lunchMenuState.foods = [food, ...lunchMenuState.foods];
  return food;
}

export async function updateLunchFood(foodId, foodData) {
  const food = await LunchApi.updateFood(foodId, foodData);
  const index = lunchMenuState.foods.findIndex((item) => item.id === foodId);
  if (index === -1) {
    lunchMenuState.foods = [food, ...lunchMenuState.foods];
  } else {
    Vue.set(lunchMenuState.foods, index, food);
  }
  return food;
}

export async function deleteLunchFood(foodId) {
  await LunchApi.deleteFood(foodId);
  lunchMenuState.foods = lunchMenuState.foods.filter((food) => food.id !== foodId);
  Object.keys(lunchMenuState.menuSchedules).forEach((dateKey) => {
    const nextFoodIds = lunchMenuState.menuSchedules[dateKey].filter((id) => id !== foodId);
    Vue.set(lunchMenuState.menuSchedules, dateKey, nextFoodIds);
  });
  return true;
}
